import * as Icons from "heroicons-react";
import { Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, matchPath, useLocation } from "react-router-dom";
import logo from "../../../logo.svg";
import { signInUser } from "../../Utility/Auth";
import { resolvePhotoUrl } from "../../Utility/resolvePhotoUrl";
import { useSettingsQuery } from "../../../services/settingsApi";
import { getDisplayLogo } from "../../Utility/packageHelper";
import "./ReportSideBar.css";
import { useState, useEffect, useMemo } from "react";

const ReportSideBar = ({ active }) => {
  const auth = signInUser();
  const { aamarId, storeSettings } = auth || {};
  const { data: dbSettings } = useSettingsQuery(aamarId, { skip: !aamarId });
  const activeSettings = dbSettings || storeSettings;
  const storeLogoSrc = getDisplayLogo(auth, dbSettings, logo);
  const lang = useSelector((state) => state.languageReducer);

  const location = useLocation();
  const currentPath = location.pathname; // This is useful for isPathActive

  const [openMenus, setOpenMenus] = useState({}); // Track which menus are open
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);

  const menuItems = useMemo(() => [
    {
      key: "salesReports",
      label: lang?.salesReports || "Sales Reports",
      icon: <Icons.ShoppingCartOutline size={22} />,
      children: [
        { label: lang?.customerWiseSales || "Customer Wise Sales", pathKey: "customerWiseSalesReport", icon: <Icons.ShoppingCartOutline size={18} /> },
        { label: lang?.customerWiseSalesReturn || "Customer Wise Sales Return", pathKey: "customerWiseSalesReturnReport", icon: <Icons.UsersOutline size={18} /> },
        { label: lang?.dateWiseSalesReturn || "Date Wise Sales Return", pathKey: "dateWiseSalesReturnReport", icon: <Icons.UsersOutline size={18} /> },
        { label: lang?.dateWiseSalesWithDue || "Date Wise Sales with Due", pathKey: "dateWiseSalesWithDueReport", icon: <Icons.ShoppingBagOutline size={18} /> },
        { label: lang?.invoiceWiseGrossProfit || "Invoice Wise Gross Profit", pathKey: "invoiceWiseGrossProfitReport", icon: <Icons.ShoppingBagOutline size={18} /> },
        { label: lang?.productWiseSales || "Product Wise Sales", pathKey: "productWiseSalesReport", icon: <Icons.ShoppingBagOutline size={18} /> },
        { label: lang?.userWiseSales || "User Wise Sales", pathKey: "userWiseSalesReport", icon: <Icons.ShoppingBagOutline size={18} /> },
      ],
    },
    {
      key: "purchaseReports",
      label: lang?.purchaseReports || "Purchase Reports",
      icon: <Icons.InboxInOutline size={22} />,
      children: [
        { label: lang?.dateWisePurchase || "Date Wise Purchase", pathKey: "dateWisePurchaseReport", icon: <Icons.CalendarOutline size={18} /> },
        { label: lang?.supplierWisePurchase || "Supplier Wise Purchase", pathKey: "supplierWisePurchaseReport", icon: <Icons.UsersOutline size={18} /> },
        { label: lang?.productWisePurchase || "Product Wise Purchase", pathKey: "productWisePurchaseReport", icon: <Icons.ArchiveOutline size={18} /> },
        { label: lang?.userWisePurchase || "User Wise Purchase", pathKey: "userWisePurchaseReport", icon: <Icons.UserOutline size={18} /> },
      ],
    },
    {
      key: "productReports",
      label: lang?.productReports || "Products Reports",
      icon: <Icons.ArchiveOutline size={22} />,
      children: [
        { label: lang?.invoiceWiseProduct || "Invoice Wise Product", pathKey: "invoiceWiseProductReport", icon: <Icons.ClipboardListOutline size={18} /> },
      ],
    },
    {
      key: "profitLossReports",
      label: lang?.profitLossReports || "Profit Loss Reports",
      icon: <Icons.ChartPieOutline size={22} />,
      children: [
        { label: lang?.profitLossReport || "Profit Loss Report", pathKey: "profitLossReport", icon: <Icons.CurrencyDollarOutline size={18} /> },
      ],
    },
  ], [lang]);

  // Helper function to check if a link is active based on current path
  const isPathActive = (pathKey) => {
    if (!pathKey) return false;
    if (currentPath === "/reports" && pathKey === "customerWiseSalesReport") {
      return true;
    }
    const fullPath = `/reports/${pathKey}`;
    // For dynamic routes (though reports don't seem to have them, good for robustness)
    if (fullPath.includes('/:id')) {
      return !!matchPath(fullPath, currentPath);
    }
    return currentPath === fullPath || currentPath.startsWith(`${fullPath}/`);
  };

  // Effect to open parent menus based on active route (using currentPath from useLocation)
  useEffect(() => {
    const newOpenMenus = {};
    menuItems.forEach((menu) => {
      if (menu.children) {
        const isChildPathActive = menu.children.some((child) => isPathActive(child.pathKey));
        if (isChildPathActive) {
          newOpenMenus[menu.key] = true;
        }
      }
    });
    setOpenMenus(newOpenMenus);
  }, [currentPath, menuItems]);

  return (
    <aside className="sticky-md-top">
      <nav className="report-sidebar">
        <div className="report-sidebar-nav text-center">
          <Link to="/dashboard" className="d-flex justify-content-center align-items-center">
            <Image
              src={storeLogoSrc}
              className="report-sidebar-logo mx-auto d-block"
              style={{ maxHeight: "45px", maxWidth: "100%", objectFit: "contain" }}
              onError={(e) => {
                e.target.src = logo;
              }}
            />
          </Link>
          
          {menuItems.map((menu) => (
            <div key={menu.key} className="report-sidebar-menu-section">
              {/* Parent Link */}
              <Link
                className={`report-sidebar-link ${isPathActive(menu.key) ? 'active' : ''}`}
                to={menu.children ? `/reports/${menu.children[0].pathKey}` : `/reports/${menu.key}`}
                onClick={() => {
                  if (menu.children) {
                    setOpenMenus(prev => ({ ...prev, [menu.key]: !prev[menu.key] }));
                  }
                }}
                onMouseEnter={() => setHoveredMenu(menu.key)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <div className="report-sidebar-menu-header">
                  <span className="report-sidebar-menu-title">
                    <span className="report-sidebar-icon">{menu.icon}</span>
                    <span>{menu.label}</span>
                  </span>
                  {menu.children && (
                    <span className="report-sidebar-icon">
                      {openMenus[menu.key] ? (
                        <Icons.ChevronDownOutline size={16} />
                      ) : (
                        <Icons.ChevronRightOutline size={16} />
                      )}
                    </span>
                  )}
                </div>
              </Link>

              {/* Submenu Links */}
              {openMenus[menu.key] && menu.children && (
                <div className="report-sidebar-submenu">
                  {menu.children.map((child, index) => (
                    <Link
                      key={index}
                      className={`report-sidebar-link report-sidebar-link-child ${isPathActive(child.pathKey) ? 'active' : ''}`}
                      to={`/reports/${child.pathKey}`}
                      onMouseEnter={() => setHoveredLink(child.pathKey)}
                      onMouseLeave={() => setHoveredLink(null)}
                    >
                      <span className="report-sidebar-icon">{child.icon}</span>
                      <span>{child.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );
};

export default ReportSideBar;
